import React from 'react';
import { X } from 'lucide-react';
import { Button } from '../../ui/button';

export const StaffPanel = ({ children, className = '', as: Tag = 'section' }) => (
  <Tag
    className={`rounded-[1.6rem] border border-white/10 bg-[#0d1011]/95 p-5
      shadow-[0_18px_60px_-40px_rgba(0,255,221,0.35)] sm:p-6 ${className}`}
  >
    {children}
  </Tag>
);

export const StaffSwitch = ({ checked, disabled, label, description, onChange }) => (
  <label className="flex cursor-pointer items-start justify-between gap-5">
    <span>
      <span className="block font-heading text-lg font-black">{label}</span>
      {description && <span className="mt-1 block text-sm leading-relaxed text-white/55">{description}</span>}
    </span>
    <span className="relative mt-0.5 shrink-0">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="peer sr-only"
      />
      <span
        className="block h-7 w-12 rounded-full border border-white/20 bg-white/10 transition
          peer-checked:border-cyan peer-checked:bg-cyan/80 peer-disabled:opacity-40
          peer-focus-visible:ring-2 peer-focus-visible:ring-cyan peer-focus-visible:ring-offset-2
          peer-focus-visible:ring-offset-black"
      />
      <span
        className="absolute left-1 top-1 h-5 w-5 rounded-full bg-white transition-transform
          peer-checked:translate-x-5 peer-checked:bg-black"
      />
    </span>
  </label>
);

export const StatusChip = ({ status }) => {
  const styles = {
    pending: 'border-cyan/40 bg-cyan/10 text-cyan',
    waiting: 'border-cyan/40 bg-cyan/10 text-cyan',
    unclaimed: 'border-white/15 bg-white/[0.05] text-white/60',
    approved: 'border-white/25 bg-white/10 text-white',
  };
  const labels = {
    pending: 'Waiting for you',
    waiting: 'Waiting for you',
    unclaimed: 'Not claimed',
    approved: 'Approved',
  };

  return (
    <span
      className={`inline-flex rounded-full border px-3 py-1 text-xs font-black uppercase
        tracking-wide ${styles[status] || styles.unclaimed}`}
    >
      {labels[status] || status}
    </span>
  );
};

export const TypedConfirmDialog = ({
  confirmText,
  confirmLabel,
  description,
  onCancel,
  onConfirm,
  open,
  title,
  value,
  onValueChange,
  busy = false,
}) => {
  if (!open) return null;
  const matches = value === confirmText;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="typed-confirm-title"
    >
      <div className="w-full max-w-lg rounded-[1.75rem] border border-red-400/35 bg-[#111415] p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="typed-confirm-title" className="font-heading text-2xl font-black">{title}</h2>
            <p className="mt-2 leading-relaxed text-white/65">{description}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            className="min-h-11 min-w-11 rounded-full text-white/60 hover:bg-white/10 hover:text-white"
            aria-label="Cancel"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        <label className="mt-6 block font-bold" htmlFor="typed-confirm-input">
          Type <span className="text-red-200">{confirmText}</span> to confirm
        </label>
        <input
          id="typed-confirm-input"
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          autoFocus
          className="mt-2 min-h-12 w-full rounded-xl border border-white/15 bg-black/35 px-4
            text-white outline-none focus:border-red-300 focus:ring-2 focus:ring-red-400/30"
        />
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            className="min-h-11 rounded-full text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={!matches || busy}
            className="min-h-11 rounded-full bg-red-500 px-6 font-black text-white hover:bg-red-400"
          >
            {busy ? 'Deleting...' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

